// Contenido SEO visible y metadatos de /detector-de-fugas
// (Detector de Fugas Completo). Una sola URL: el quiz no genera rutas por
// pregunta. Bajo la card queda un único bloque de ratios y 4 FAQ.
import { SITE_URL } from "@/lib/site-url";

export type SeoLang = "es" | "en";

export const ANALISIS_PATH_ES = "/detector-de-fugas";
export const ANALISIS_PATH_EN = "/en/leak-detector";

export const ANALISIS_COPY = {
  title: {
    es: "Detector de Fugas Completo para comercios | Retail Lemon",
    en: "Complete Leak Detector for local shops | Retail Lemon",
  },
  description: {
    es: "Diagnóstico por área de tu negocio: finanzas, local, equipo, producto y ventas. Empieza sin cuenta. El detalle de cada módulo es Club Pro.",
    en: "Area-by-area diagnosis of your business: finance, premises, team, product and sales. Start without an account. Module detail is Club Pro.",
  },
  ogTitle: {
    es: "Detector de Fugas Completo para comercios a pie de calle",
    en: "Complete Leak Detector for high-street businesses",
  },
  ogDescription: {
    es: "¿El alquiler o el personal se comen el mes? Mide las fugas de tu local. Retail Lemon.",
    en: "Is rent or staff eating your month? Measure your premises' leaks. Retail Lemon.",
  },
  h1: { es: "Detector de Fugas Completo", en: "Complete Leak Detector" },
  sub: {
    es: "Analiza tu negocio por áreas",
    en: "Analyse your business by area",
  },
  micro: {
    es: "Gratis empezar · Primeros Pasos con cuenta · Detalle en Club Pro",
    en: "Free to start · First Steps with an account · Detail in Club Pro",
  },
} as const;

/** H2 único con los ratios de referencia (3 párrafos, sin muro de titulares). */
export const RATIOS_COPY = {
  heading: { es: "Qué ratios usamos", en: "Which ratios we use" },
  paragraphs: {
    es: [
      "Alquiler y personal marcan el punto de partida. En un local a pie de calle, un alquiler por debajo del 8–12% de la facturación suele ser asumible; por encima del 15–18% condiciona el margen. El personal, con Seguridad Social de empresa, se mueve entre el 25% y el 35% de las ventas en hostelería y entre el 15% y el 25% en tienda. Si no lo tienes medido, ya es una fuga.",
      "Producto y stock son la segunda capa. El coste de mercancía o food cost ronda el 22–28% en bar y cafetería y el 28–35% en restaurante; sin escandallo o ficha de coste estás adivinando el margen. En tienda, una rotación sana suele estar entre 4 y 6 veces al año: género parado más de tres meses (o huecos de agenda en estética) inmoviliza dinero.",
      "Equipo, caja y clientes cierran el diagnóstico. Si una semana sin ti para el local, la fuga es de sistema: turnos, apertura y cierre por escrito son el mínimo. Ver el saldo del banco no es un cierre de caja, y sin ticket medio ni recurrencia a 90 días vendes a ciegas. El diagnóstico pregunta por rangos, no por céntimos, y funciona también si aún no has abierto.",
    ],
    en: [
      "Rent and staff set the starting point. For a high-street premises, rent below 8–12% of revenue is usually manageable; above 15–18% it conditions your margin. Staff cost, including employer social security, sits between 25% and 35% of sales in hospitality and 15% to 25% in retail. If you haven't measured it, that is already a leak.",
      "Product and stock are the second layer. Cost of goods or food cost is around 22–28% in bars and cafés and 28–35% in restaurants; without a cost sheet you're guessing the margin. In retail, healthy rotation is usually 4 to 6 times a year: goods sitting still for over three months (or diary gaps in beauty) tie up cash.",
      "Team, cash and customers close the diagnosis. If a week without you stops the shop, the leak is systemic: written shifts, opening and closing are the minimum. Looking at the bank balance isn't a cash close, and without an average ticket or 90-day repeat data you sell blind. The diagnosis asks for ranges, not cents, and works even if you haven't opened yet.",
    ],
  },
} as const;

export interface SeoBlock { anchor: string; question: { es: string; en: string }; text: { es: string; en: string } }

export const SEO_FAQ: SeoBlock[] = [
  {
    anchor: "faq-cuenta",
    question: { es: "¿Puedo hacerlo sin registrarme?", en: "Can I do it without signing up?" },
    text: {
      es: "Sí. La cuenta sirve para guardar y abrir Primeros Pasos.",
      en: "Yes. The account is for saving your result and unlocking First Steps.",
    },
  },
  {
    anchor: "faq-club-pro",
    question: { es: "¿Qué veo si no soy Club Pro?", en: "What do I see if I'm not Club Pro?" },
    text: {
      es: "El semáforo. La orientación y las acciones de cada módulo son Club Pro.",
      en: "The traffic light. Each module's orientation and actions are Club Pro.",
    },
  },
  {
    anchor: "faq-express",
    question: { es: "¿En qué se diferencia del Express?", en: "How is it different from the Express?" },
    text: {
      es: "El Express son 6 preguntas en la Home. Este recorre cada área.",
      en: "The Express is 6 questions on the home page. This one walks through every area.",
    },
  },
  {
    anchor: "faq-sectores",
    question: { es: "¿Qué sectores cubre?", en: "Which sectors does it cover?" },
    text: {
      es: "Comercios y locales a pie de calle (tienda, hostelería, estética, etc.).",
      en: "Shops and high-street premises (retail, hospitality, beauty, etc.).",
    },
  },
];

export const faqJsonLd = (lang: SeoLang) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: SEO_FAQ.map((b) => ({
    "@type": "Question",
    name: b.question[lang],
    acceptedAnswer: { "@type": "Answer", text: b.text[lang] },
  })),
});

/** `head()` SSR de la ruta (meta sin links: canonical/hreflang los emite <SEO />). */
export const analisisCompletoHead = (lang: SeoLang) => {
  const url = `${SITE_URL}${lang === "es" ? ANALISIS_PATH_ES : ANALISIS_PATH_EN}`;
  const image = `${SITE_URL}/assets/og/og-leaks.jpg`;
  return {
    meta: [
      { title: ANALISIS_COPY.title[lang] },
      { name: "description", content: ANALISIS_COPY.description[lang] },
      { property: "og:title", content: ANALISIS_COPY.title[lang] },
      { property: "og:description", content: ANALISIS_COPY.ogDescription[lang] },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: lang === "es" ? "es_ES" : "en_US" },
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: ANALISIS_COPY.title[lang] },
      { name: "twitter:description", content: ANALISIS_COPY.ogDescription[lang] },
      { name: "twitter:image", content: image },
      // robots lo emite <SEO /> (index,follow; noindex,follow en el resultado).
    ],
  };
};
